import { ComponentPropsWithoutRef, MouseEvent, forwardRef } from "react";
import cx from "classnames";
import styles from "./Alert.module.scss";
import { Button } from "../Button";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";
import Info from "@servicetitan/hammer-icon/mdi/round/info.svg";
import Warning from "@servicetitan/hammer-icon/mdi/round/warning.svg";
import Danger from "@servicetitan/hammer-icon/mdi/round/error.svg";
import Success from "@servicetitan/hammer-icon/mdi/round/check_circle.svg";
import { Icon } from "../Icon";
import { OnCloseProps, LayoutUtilProps, Status, Svg } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";

/**
 * Props for the Alert component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends LayoutUtilProps
 * @extends OnCloseProps
 */
export type AlertProps = ComponentPropsWithoutRef<"div"> &
  LayoutUtilProps &
  OnCloseProps & {
    /**
     * The visual style of an alert.
     * @default info
     */
    status?: Status;
    /**
     * The title text displayed in the alert.
     */
    title: string;
  };

const statusIconMap: Record<NonNullable<AlertProps["status"]>, Svg> = {
  info: Info,
  success: Success,
  warning: Warning,
  danger: Danger,
};

/**
 * Alert component for displaying important messages to users.
 *
 * Features:
 * - Supports different visual styles (info, success, warning, danger)
 * - Optional close button for dismissible alerts
 * - Customizable title and content
 * - Accessible with proper ARIA roles
 * - Includes status-specific icons
 * - Supports layout utilities for positioning and spacing
 *
 * @example
 * <Alert
 *   title="System Update"
 *   onClose={() => console.log('Alert closed')}
 * >
 *   The system will be updated tonight.
 * </Alert>
 *
 * @example
 * <Alert
 *   status="success"
 *   title="Operation Complete"
 * >
 *   Your changes have been saved successfully.
 * </Alert>
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>((props, ref) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    children,
    className,
    title,
    status = "info",
    style,
    onClose,
    ...rest
  } = componentProps;
  const alertCx = cx(styles["alert"], className, {
    [styles["success"]]: status === "success",
    [styles["warning"]]: status === "warning",
    [styles["danger"]]: status === "danger",
  });

  const styleCombined = {
    ...style,
    ...layoutStyles,
  };

  return (
    <div
      data-anv="alert"
      role="status"
      className={alertCx}
      style={styleCombined}
      {...rest}
      ref={ref}
    >
      <div className={styles["top"]}>
        <span className={styles["title-container"]}>
          {
            <Icon
              size="large"
              className={styles["status-icon"]}
              svg={statusIconMap[status ?? "info"]}
            />
          }
          {title}
        </span>
        {!!onClose && (
          <span className={styles["close-container"]}>
            <Button
              icon={Close}
              size="small"
              appearance="ghost"
              className={styles["close-button"]}
              onClick={(e: MouseEvent<HTMLButtonElement>) => onClose(e)}
              aria-label={`Dismiss ${status} alert`}
            />
          </span>
        )}
      </div>
      {children && <div className={styles["content"]}>{children}</div>}
    </div>
  );
});

Alert.displayName = "Alert";
